<script setup lang="ts">
/**
 * PAGE CAGNOTTE — haut de la future page cagnotte, deux variantes retenues par
 * Karim (2026-10-12) :
 *  · « mosaique » (A) : titre au-dessus, mosaïque 1 grande + 4 petites photos
 *                       (façon Airbnb / Dift) ;
 *  · « immersif » (B) : grande photo plein cadre, titre et association posés
 *                       dessus (même langage que les cartes vitrine) ; on fait
 *                       défiler les photos au doigt (Karim 2026-10-13) ;
 *  · « banniere »  (C) : titre seul — la bannière à flèches est dans la
 *                       colonne de gauche (PageCagnotteBanniere).
 */
import { useRechercheHelpers } from "~/composables/useRechercheHelpers";
import { useDemoAccueil } from "~/composables/useDemoAccueil";
import { themeAffiche } from "~/utils/themesAffiches";
import PageCagnotteGalerie from "./PageCagnotteGalerie.vue";
import type { CagnotteAffichee, ModelePageCagnotte } from "./types";

const props = withDefaults(
  defineProps<{
    cagnotte: CagnotteAffichee;
    variante: ModelePageCagnotte;
    /** « immersif » sur téléphone : photo collée en haut de l'écran, la barre
     *  de navigation du site flotte dessus. Faux dans la démo de l'accueil
     *  (pas de barre) : le bouton « N photos » y descend sous l'îlot du
     *  téléphone simulé. */
    sousBarre?: boolean;
  }>(),
  { sousBarre: true },
);
const { getImageUrl } = useRechercheHelpers();
// Démo de la home : la photo n'y est pas l'image principale (sous le pli de
// la home) → chargée sans priorité, comme les autres images de la démo.
const { estIntegree } = useDemoAccueil();
const { formatDate } = useFormatLangue();
const localePath = useLocalePath();
/** Page de l'association, dans la langue du visiteur. */
const lienAssociation = computed(() =>
  localePath({ path: `/associations/${props.cagnotte.organization_slug}` }),
);

const images = computed(() => {
  const ids = [props.cagnotte.image, ...(props.cagnotte.gallery ?? []).map((g) => g.directus_files_id)];
  return [...new Set(ids.filter((x): x is string => !!x))];
});
const pastille = computed(() =>
  props.cagnotte.category && props.cagnotte.category !== "autre" ? themeAffiche(props.cagnotte.category) : "",
);
const galerie = ref<number | null>(null);

// ─── B · Immersive : photos qu'on fait défiler (aimantées, une par écran) ─
const defileur = ref<HTMLElement | null>(null);
const photoAffichee = ref(0);
function majPhoto() {
  const el = defileur.value;
  if (el && el.clientWidth) photoAffichee.value = Math.round(el.scrollLeft / el.clientWidth);
}
function allerPhoto(i: number) {
  const el = defileur.value;
  if (el) el.scrollTo({ left: i * el.clientWidth, behavior: "smooth" });
}
/** Flèches : photo voisine, en boucle (après la dernière, la première). */
function photoVoisine(pas: 1 | -1) {
  const n = images.value.length;
  allerPhoto((photoAffichee.value + pas + n) % n);
}

// Glisser à la SOURIS sur ordinateur (doigt et pavé tactile défilent déjà
// nativement) : la photo suit la main, puis on passe à la voisine si on a
// tiré de plus de 60 px (ou vite), sinon elle revient.
const glisseSouris = ref(false);
let depart: { x: number; scroll: number; t: number; photo: number } | null = null;
let aGlisse = false;
function debutSouris(e: PointerEvent) {
  if (e.pointerType !== "mouse" || e.button !== 0 || !defileur.value) return;
  depart = { x: e.clientX, scroll: defileur.value.scrollLeft, t: performance.now(), photo: photoAffichee.value };
  aGlisse = false;
}
function pendantSouris(e: PointerEvent) {
  const el = defileur.value;
  if (!depart || !el) return;
  const dx = e.clientX - depart.x;
  if (!glisseSouris.value && Math.abs(dx) > 5) {
    glisseSouris.value = true; // aimantation coupée pendant le glisser
    aGlisse = true;
    el.setPointerCapture(e.pointerId);
  }
  if (glisseSouris.value) el.scrollLeft = depart.scroll - dx;
}
function finSouris(e: PointerEvent) {
  const el = defileur.value;
  if (!depart || !el) return;
  const dx = e.clientX - depart.x;
  const rapide = Math.abs(dx) / Math.max(1, performance.now() - depart.t) > 0.5;
  const pas = glisseSouris.value && (Math.abs(dx) > 60 || rapide) ? (dx < 0 ? 1 : -1) : 0;
  const cible = Math.min(images.value.length - 1, Math.max(0, depart.photo + pas));
  depart = null;
  if (!glisseSouris.value) return;
  glisseSouris.value = false;
  allerPhoto(cible);
}
/** Un glisser n'est pas un clic : il n'ouvre pas la visionneuse. */
function bloquerClic(e: MouseEvent) {
  if (aGlisse) {
    e.stopPropagation();
    e.preventDefault();
    aGlisse = false;
  }
}

/** Mosaïque : 5 photos (1 grande + 4), sinon 3 (1 grande + 2), sinon ce qu'on a. */
const mosaique = computed(() => {
  const n = images.value.length;
  return images.value.slice(0, n >= 5 ? 5 : n >= 3 ? 3 : n);
});
function classeTuile(i: number) {
  const n = mosaique.value.length;
  if (n < 3) return "";
  if (i === 0) return "col-span-4 row-span-2 sm:col-span-2";
  return n === 3 ? "hidden sm:block sm:col-span-2" : "hidden sm:block";
}

const titreStyle =
  "font-family: var(--font-body) !important; font-weight: 800 !important; line-height: 1.08 !important; letter-spacing: -0.025em !important";
</script>

<template>
  <!-- ═══ A · Mosaïque / C · Bannière : titre au-dessus ═══ -->
  <div v-if="variante === 'mosaique' || variante === 'banniere'" class="pt-8 lg:pt-10">
    <div class="flex flex-wrap items-center gap-2">
      <span v-if="pastille" class="px-3 py-1 rounded-full bg-[#e8eeff] text-[#0c3097] text-xs" style="font-weight: 700 !important">{{ pastille }}</span>
      <span v-if="cagnotte.is_verified" class="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-emerald-50 text-emerald-700 text-xs" style="font-weight: 700 !important">
        <UIcon name="i-lucide-badge-check" class="size-3.5" /> {{ $t("campagne.pageCagnotte.verifiee") }}
      </span>
    </div>
    <h1 class="mt-4 max-w-4xl text-[30px] sm:text-[40px] lg:text-[48px] text-(--color-ink)" :style="titreStyle">
      {{ cagnotte.title }}
    </h1>
    <a :href="lienAssociation" class="mt-4 inline-flex items-center gap-2.5 no-underline group">
      <OrgLogo :logo="cagnotte.organization_logo" :name="cagnotte.organization_name" size-class="w-9 h-9" rounded-class="rounded-full" ring-class="border border-gray-200" :width="72" />
      <i18n-t keypath="campagne.pageCagnotte.par" tag="span" class="text-[15px] text-gray-600"><template #asso><span class="text-(--color-ink) group-hover:underline" style="font-weight: 700 !important">{{ cagnotte.organization_name }}</span></template></i18n-t>
      <!-- Masqué sur téléphone : collé au nom, « depuis le … » s'y lisait comme
           l'âge de l'association (Karim 2026-10-30). -->
      <span v-if="cagnotte.date_created" class="hidden sm:inline text-sm text-gray-400">· {{ $t("campagne.pageCagnotte.depuisLe", { date: formatDate(cagnotte.date_created, { format: "medium" }) }) }}</span>
    </a>

    <div
      v-if="variante === 'mosaique' && images.length"
      class="relative mt-7 grid gap-2 rounded-[28px] overflow-hidden h-[300px] sm:h-[420px] lg:h-[480px]"
      :class="mosaique.length >= 3 ? 'grid-cols-4 grid-rows-2' : mosaique.length === 2 ? 'grid-cols-2' : 'grid-cols-1'"
    >
      <button
        v-for="(img, i) in mosaique"
        :key="img"
        type="button"
        class="group relative overflow-hidden bg-gray-100"
        :class="classeTuile(i)"
        @click="galerie = i"
      >
        <img :src="getImageUrl(img, undefined, i === 0 ? 1400 : 700)" alt="" class="w-full h-full object-cover transition-[scale] duration-[1200ms] ease-out group-hover:scale-[1.04]" :loading="i === 0 ? 'eager' : 'lazy'" />
        <span class="absolute inset-0 bg-black/0 group-hover:bg-black/10 transition-colors" />
      </button>
      <button
        v-if="images.length > 1"
        type="button"
        class="absolute bottom-4 right-4 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/95 text-sm text-(--color-ink) shadow-md hover:bg-white"
        style="font-weight: 600 !important"
        @click="galerie = 0"
      >
        <UIcon name="i-lucide-images" class="size-4" /> {{ $t("campagne.pageCagnotte.voirLesPhotos", { n: images.length }) }}
      </button>
    </div>
  </div>

  <!-- ═══ B · Immersive ═══
       Téléphone : collée en haut de l'écran, sous la barre de navigation qui
       flotte dessus (boutons du haut décalés sous la barre), et la MOITIÉ de
       l'écran en hauteur, pas plus (Karim 2026-10-13). Ordinateur : carte
       arrondie sous la barre. -->
  <div
    v-else
    class="relative -mx-5 sm:-mx-8 lg:mx-0 lg:mt-6 lg:rounded-[32px] overflow-hidden h-[50svh] lg:h-[62vh] lg:min-h-[440px] lg:max-h-[640px] bg-[#0c3097]"
  >
    <!-- Photos côte à côte : glisser au doigt (ou au pavé tactile) pour passer
         à la suivante ; un appui ouvre la visionneuse plein écran. -->
    <div
      ref="defileur"
      class="absolute inset-0 flex overflow-x-auto overscroll-x-contain select-none [scrollbar-width:none] [&::-webkit-scrollbar]:hidden"
      :class="[glisseSouris ? 'cursor-grabbing' : 'snap-x snap-mandatory', images.length > 1 ? 'lg:cursor-grab' : '']"
      @scroll.passive="majPhoto"
      @pointerdown="debutSouris"
      @pointermove="pendantSouris"
      @pointerup="finSouris"
      @pointercancel="finSouris"
      @click.capture="bloquerClic"
    >
      <button
        v-for="(img, i) in images"
        :key="img"
        type="button"
        class="relative h-full w-full shrink-0 snap-center snap-always"
        :aria-label="$t('campagne.pageCagnotte.voirPhoto', { n: i + 1, total: images.length })"
        @click="galerie = i"
      >
        <img
          :src="getImageUrl(img, undefined, i === 0 ? 2000 : 1400)"
          alt=""
          draggable="false"
          :loading="i === 0 && !estIntegree ? 'eager' : 'lazy'"
          class="absolute inset-0 w-full h-full object-cover"
        />
      </button>
    </div>
    <!-- Le voile et le texte laissent passer le doigt jusqu'aux photos. -->
    <div class="pointer-events-none absolute inset-0 bg-gradient-to-t from-black/85 via-black/35 to-black/10" />
    <div
      class="pointer-events-none absolute inset-x-0 bottom-0 p-6 sm:p-10 lg:p-12 text-white lg:pr-[440px]"
      :class="images.length > 1 ? 'pb-9 sm:pb-12' : ''"
    >
      <div class="flex flex-wrap items-center gap-2">
        <span v-if="pastille" class="px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-xs" style="font-weight: 700 !important">{{ pastille }}</span>
        <span v-if="cagnotte.is_verified" class="inline-flex items-center gap-1 px-3 py-1 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-xs" style="font-weight: 700 !important">
          <UIcon name="i-lucide-badge-check" class="size-3.5" /> {{ $t("campagne.pageCagnotte.verifieeCourt") }}
        </span>
      </div>
      <h1 class="mt-4 max-w-3xl text-[30px] sm:text-[42px] lg:text-[52px] text-white" :style="titreStyle">
        {{ cagnotte.title }}
      </h1>
      <a :href="lienAssociation" class="pointer-events-auto mt-5 inline-flex items-center gap-2.5 no-underline">
        <OrgLogo :logo="cagnotte.organization_logo" :name="cagnotte.organization_name" size-class="w-9 h-9" rounded-class="rounded-full" ring-class="border border-white/60" :width="72" />
        <i18n-t keypath="campagne.pageCagnotte.par" tag="span" class="text-[15px] text-white/80"><template #asso><span class="text-white hover:underline" style="font-weight: 700 !important">{{ cagnotte.organization_name }}</span></template></i18n-t>
      </a>
    </div>
    <button
      v-if="images.length > 1"
      type="button"
      class="absolute right-5 lg:right-auto lg:left-12 lg:top-8 inline-flex items-center gap-2 px-4 py-2 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-sm text-white hover:bg-white/30"
      :class="sousBarre ? 'top-[88px]' : 'top-16'"
      style="font-weight: 600 !important"
      @click="galerie = 0"
    >
      <UIcon name="i-lucide-images" class="size-4" /> {{ $t("campagne.pageCagnotte.nPhotos", { n: images.length }) }}
    </button>
    <!-- Flèches (ordinateur), en haut à droite : même verre dépoli que le
         bouton « N photos ». Sur téléphone, on fait glisser au doigt. -->
    <div v-if="images.length > 1" class="absolute top-8 right-8 hidden lg:flex items-center gap-2">
      <button
        type="button"
        class="size-10 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white flex items-center justify-center hover:bg-white/30 transition-colors"
        :aria-label="$t('campagne.pageCagnotte.photoPrecedente')"
        @click="photoVoisine(-1)"
      >
        <UIcon name="i-lucide-chevron-left" class="size-5" />
      </button>
      <button
        type="button"
        class="size-10 rounded-full bg-white/20 backdrop-blur-md border border-white/30 text-white flex items-center justify-center hover:bg-white/30 transition-colors"
        :aria-label="$t('campagne.pageCagnotte.photoSuivante')"
        @click="photoVoisine(1)"
      >
        <UIcon name="i-lucide-chevron-right" class="size-5" />
      </button>
    </div>
    <!-- Points : quelle photo est affichée (un appui y va). Téléphone : tout
         en bas, centrés (Karim 2026-10-13). Ordinateur : sous le bouton
         « N photos ». -->
    <div
      v-if="images.length > 1"
      class="absolute bottom-3 left-1/2 -translate-x-1/2 lg:bottom-auto lg:translate-x-0 lg:top-[84px] lg:left-[52px] flex items-center gap-1.5"
    >
      <button
        v-for="(img, i) in images"
        :key="img"
        type="button"
        class="h-1.5 rounded-full transition-all duration-300"
        :class="i === photoAffichee ? 'w-5 bg-white' : 'w-1.5 bg-white/50'"
        :aria-label="$t('campagne.pageCagnotte.photoN', { n: i + 1 })"
        @click="allerPhoto(i)"
      />
    </div>
  </div>

  <PageCagnotteGalerie v-if="galerie !== null" :images="images" :depart="galerie" @fermer="galerie = null" />
</template>
